import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { DEMO_BRANDING } from '@hakshuri/domain';
import { Bdi } from './Bdi.tsx';
import { authLink } from './account/supabase.ts';
import { BottomBar, BottomBarProvider, type Tab } from './BottomBar.tsx';
import { ScreenBoundary } from './ScreenBoundary.tsx';
import { SideSlotContext } from './SideNav.tsx';
import { ToastSlotContext } from './Toast.tsx';
import { UsageWarningBanner } from './account/UsagePanel.tsx';
import { UpdateBanner } from './UpdateBanner.tsx';

// כל מסך בקובץ JS משלו (D14 ג׳ 10א): הטעינה הראשונה בטלפון מורידה רק את
// המסך שנפתח, ו־pdf.js נטען רק כשפותחים תוכנית (features/pdf.ts).
const VisitsScreen = lazy(() =>
  import('./visits/VisitsScreen.tsx').then((m) => ({ default: m.VisitsScreen })),
);
const SiteFileScreen = lazy(() =>
  import('./sitefile/SiteFileScreen.tsx').then((m) => ({ default: m.SiteFileScreen })),
);
const AccountScreen = lazy(() =>
  import('./account/AccountScreen.tsx').then((m) => ({ default: m.AccountScreen })),
);

/*
 * שלוש לשוניות (`Tab` — BottomBar.tsx). לשונית ״הדגמה מקומית (שלב 1)״ — מסך הביקור של שלב 1,
 * שמירה במכשיר בלי שרת — הוסרה ב־6ה-1 (A-43 נסגרה): הבדיקות שלה עברו למסך הביקור האמיתי.
 */

/** כתובות של לשונית — מקישור פנימי (״עדכון בתיק האתר״) או מקישור ישיר. */
const TAB_HASHES = new Set(['#visits', '#site', '#account']);

/** קישור מדוא״ל (הזמנה, איפוס) או בקשה מפורשת פותחים את מסך החשבון. */
function initialTab(): Tab {
  if (authLink.type !== null || authLink.error !== null) return 'account';
  if (window.location.hash === '#account') return 'account';
  return window.location.hash === '#site' ? 'site' : 'visits';
}

/**
 * שלד האפליקציה. מגבלות ההדגמה מוצגות בראש כל מסך ואינן מוסתרות
 * בתיעוד — הדרישה היא שלא יוצג מה שלא מומש כפיצ׳ר עובד.
 *
 * 6ג (RQ-J11): במסך רחב העמודה הצדדית (`.side`) מציגה את הניווט של המסך הפתוח (SideNav.tsx).
 * אותם רכיבים בכל רוחב — הסידור ב־styles.css.
 *
 * 6ו (RQ-J08): קישור דילוג ל־main (הפקד הראשון ב־Tab); פס ההדגמה בשורה אחת ו״מה זה אומר״ מקופל.
 *
 * 6ו-ב (D31, A-180): ראש המותג קומפקטי — לוגו 24px ושם המותג (ה־h1, A-172) 15px/600 בשורה
 * אחת עם צ׳יפ ״הדגמה״; שם המוצר הלטיני (`.brand-latin`) מוסתר ב־CSS. הכותרת של כל מסך היא
 * הכותרת הגדולה שמתחת לפס הניווט (NavBar.tsx) — כל מסך מרנדר אותה בעצמו.
 *
 * 6ו-ב חלק 3 (A-184, A-185, A-187): הסרגל התחתון (BottomBar.tsx) בכל מצב ובכל רוחב — הלשוניות
 * (tabindex נודד + חצים, A-175) יצאו מהעמודה הצדדית ומהשורה שבטלפון אל הסרגל; `.side` נשאר רק
 * עם `side-context`. מבנה השלד: `div.app > a.skip-link · div.app-content (ראש המותג, הפסים, main,
 * footer, sentinel) · nav.action-bar · div.toast-root` — הסרגל וההודעות הם **אחים של העטיפה**,
 * לא בתוכה: חלק 5 מותמר (`transform`) את העטיפה כשחלונית פתוחה, ו־`position: fixed` בתוך
 * אלמנט מותמר נמדד ממנו ולא מהחלון. הלחיצה על לשונית = `select()` (hash, A-149 — בלי שינוי).
 */
export function App(): ReactElement {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [sideSlot, setSideSlot] = useState<HTMLDivElement | null>(null);
  const [toastSlot, setToastSlot] = useState<HTMLDivElement | null>(null);
  const main = useRef<HTMLElement>(null);
  // sentinel בסוף התוכן — הסרגל התחתון מקבל צל כשיש תוכן מתחתיו (useStuck).
  const barSentinel = useRef<HTMLDivElement>(null);
  // A-149: קישור פנימי משאיר את הלשונית בכתובת (#site), ואז אותו קישור לא היה עובד שוב — הכתובת
  // לא השתנתה. לחיצה על לשונית מסירה אותה (replaceState, בלי רשומה בהיסטוריה). רענון נפתח
  // כרגיל ב״ביקורים״ — ושם מנוע הסנכרון שולח מיד שינויים שממתינים בתור.
  const select = useCallback((next: Tab) => {
    setTab(next);
    if (TAB_HASHES.has(window.location.hash))
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search,
      );
  }, []);
  // קישור פנימי (למשל ״עדכון בתיק האתר״ מהביקור, D22) משנה את הלשונית.
  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === '#site') setTab('site');
      else if (window.location.hash === '#account') setTab('account');
      else if (window.location.hash === '#visits') setTab('visits');
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return (
    <div className="app">
      <BottomBarProvider>
        <ToastSlotContext.Provider value={toastSlot}>
          <a
            className="skip-link"
            href="#main"
            data-testid="skip-link"
            onClick={(e) => {
              // בלי שינוי הכתובת (hash של לשונית — A-149): הפוקוס עובר ישר לתוכן.
              e.preventDefault();
              main.current?.focus({ preventScroll: true });
              main.current?.scrollIntoView({ block: 'start' });
            }}
          >
            דילוג לתוכן
          </a>
          <div className="app-content" data-testid="app-content">
            <header className="topbar">
              <div className="brand">
                <Logo />
                <div>
                  <h1 className="brand-name">
                    <Bdi>{DEMO_BRANDING.organizationName}</Bdi>
                  </h1>
                  <div className="brand-latin">
                    <Bdi>{DEMO_BRANDING.productName}</Bdi>
                  </div>
                </div>
              </div>
              <span className="demo-pill">הדגמה</span>
            </header>

            <div className="demo-banner" data-testid="demo-banner">
              <strong>נתוני הדגמה בלבד</strong> — תוכן הצ׳קליסט הוא טיוטה שהפרקים המקצועיים בה{' '}
              <strong>לא אומתו</strong>, ואין להסתמך עליו בשטח.
              <details>
                <summary>מה זה אומר</summary>
                <p>
                  ביקורים, תיק אתר והרשאות פונים לפרויקט Supabase. ביקור שנפתח עם רשת ממשיך לעבוד גם
                  בלי רשת, והשינויים נשלחים כשהיא חוזרת. פתיחת ביקור חדש, תיק אתר, הפקת דוח (דוח
                  ביקור, דוח תיקונים, התראת סיכון) ומחיקה לצמיתות — דורשים רשת. האפליקציה אינה שולחת
                  דוחות ואינה רושמת מסירה: הדוח נמסר בשיתוף מתפריט המכשיר.
                </p>
              </details>
            </div>

            <UpdateBanner />
            <UsageWarningBanner onOpen={() => select('account')} />

            <main className="layout" id="main" tabIndex={-1} ref={main}>
              <div className="side" data-testid="side-nav">
                <div className="side-context" ref={setSideSlot} data-testid="side-context" />
              </div>
              <div className="content">
                <SideSlotContext.Provider value={sideSlot}>
                  <ScreenBoundary>
                    <Suspense fallback={<p className="muted">טוען את המסך…</p>}>
                      {tab === 'visits' ? (
                        <VisitsScreen />
                      ) : tab === 'site' ? (
                        <SiteFileScreen />
                      ) : (
                        <AccountScreen />
                      )}
                    </Suspense>
                  </ScreenBoundary>
                </SideSlotContext.Provider>
              </div>
            </main>

            <footer className="footer small muted">
              <Bdi>{DEMO_BRANDING.footerText}</Bdi>
            </footer>
            <div ref={barSentinel} className="action-bar-sentinel" aria-hidden="true" />
          </div>
          <BottomBar tab={tab} onSelect={select} sentinel={barSentinel} />
          <div className="toast-root" ref={setToastSlot} />
        </ToastSlotContext.Provider>
      </BottomBarProvider>
    </div>
  );
}

/** לוגו וקטורי מקורי — מגן וקווי מבנה. אין סמל ממשלתי ואין מיתוג חיצוני. 24px (6ו-ב, A-180). */
function Logo(): ReactElement {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 48 48"
      role="img"
      aria-label="חכשורי בטיחות"
      className="logo"
    >
      <path
        d="M24 3 42 9v16c0 10-7.5 17.5-18 20C13.5 42.5 6 35 6 25V9L24 3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path
        d="M15 31V20l9-5 9 5v11"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path d="M15 25h18M24 15v16" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  );
}
